import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import { Mix } from '../types/Mix';
import { Show } from '../types/Show';
import { debugWarn } from '../utils/debugLog';
import listeningProgressService from '../services/listeningProgressService';
import { DEFAULT_SHOW_FALLBACK_IMAGE } from '../constants/images';

export interface AudioTrack {
  id: string;
  title: string;
  artist: string;
  url: string;
  isLive?: boolean;
  contentType?: 'live' | 'show' | 'mix';
  artwork?: string;
  duration?: number; // Duration in seconds for recorded shows
  show?: Show; // Reference to the full show data
  mix?: Mix; // Reference to the full mix data
  resumeFrom?: number;
}

interface AudioPlayerContextType {
  // Current track state
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  volume: number;
  
  // Playback position (for recorded shows)
  currentTime: number;
  duration: number;
  
  // Player actions
  playTrack: (track: AudioTrack) => void;
  playShow: (show: Show) => void;
  playLiveStream: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVolume: (volume: number) => void;
  
  // Scrubbing controls (for recorded shows)
  seekTo: (time: number) => void;
  updateCurrentTime: (time: number) => void;
  updateDuration: (duration: number) => void;
  
  // Player visibility
  isPlayerVisible: boolean;
  showPlayer: () => void;
  hidePlayer: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  // Initialize with default live stream track so player is always visible
  const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>({
    id: 'live-stream',
    title: 'Live Stream',
    artist: 'Eruption Radio',
    url: process.env.REACT_APP_STREAM_PRIMARY || 'http://streaming04.liveboxstream.uk:8116/stream',
    isLive: true,
    contentType: 'live',
    artwork: DEFAULT_SHOW_FALLBACK_IMAGE
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.8);
  const [isPlayerVisible, setIsPlayerVisible] = useState(true); // Always visible now
  
  // Playback position state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const playTrack = useCallback((track: AudioTrack) => {
    setCurrentTrack(track);
    setIsPlaying(true);
    setIsPlayerVisible(true);
    // Don't set loading here - let the audio player component handle loading state
    
    // Reset playback position for new tracks
    const initialPosition = track.resumeFrom ?? 0;
    setCurrentTime(initialPosition);
    setDuration(track.duration || 0);
  }, []);

  const playShow = useCallback((show: Show) => {
    if (!show.audioUrl) {
      debugWarn('Show has no audio URL available');
      return;
    }

    const progress = show.show_id ? listeningProgressService.getProgress(show.show_id) : null;
    const resumeFrom = progress?.positionSeconds ?? 0;

    const showTrack: AudioTrack = {
      id: `show-${show.show}-${show.date_start_time}`,
      title: show.show,
      artist: show.show,
      url: show.audioUrl,
      isLive: false,
      contentType: 'show',
      artwork: show.images["300x300"] || show.images["600x600"] || DEFAULT_SHOW_FALLBACK_IMAGE,
      duration: show.selected_length_seconds ?? show.duration,
      show: show,
      resumeFrom: resumeFrom > 0 ? resumeFrom : undefined
    };
    
    playTrack(showTrack);
  }, [playTrack]);

  const playLiveStream = useCallback(() => {
    const liveTrack: AudioTrack = {
      id: 'live-stream',
      title: 'Live Stream',
      artist: 'Eruption Radio',
      url: process.env.REACT_APP_STREAM_PRIMARY || '',
      isLive: true,
      contentType: 'live',
      artwork: DEFAULT_SHOW_FALLBACK_IMAGE
    };
    playTrack(liveTrack);
    // Trigger actual playback
    setIsPlaying(true);
  }, [playTrack]);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const resume = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const stop = useCallback(() => {
    setIsPlaying(false);
    setCurrentTrack(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlayerVisible(false);
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const seekTo = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  const updateCurrentTime = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  const updateDuration = useCallback((duration: number) => {
    setDuration(duration);
  }, []);

  const showPlayer = useCallback(() => {
    setIsPlayerVisible(true);
  }, []);

  const hidePlayer = useCallback(() => {
    setIsPlayerVisible(false);
  }, []);

  const value: AudioPlayerContextType = useMemo(() => ({
    currentTrack,
    isPlaying,
    volume,
    currentTime,
    duration,
    playTrack,
    playShow,
    playLiveStream,
    pause,
    resume,
    stop,
    setVolume,
    seekTo,
    updateCurrentTime,
    updateDuration,
    isPlayerVisible,
    showPlayer,
    hidePlayer,
  }), [
    currentTrack,
    isPlaying,
    volume,
    currentTime,
    duration,
    playTrack,
    playShow,
    playLiveStream,
    pause,
    resume,
    stop,
    setVolume,
    seekTo,
    updateCurrentTime,
    updateDuration,
    isPlayerVisible,
    showPlayer,
    hidePlayer,
  ]);

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = (): AudioPlayerContextType => {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};
